"use client";

import { AlertCircle, FileText, User, RefreshCw } from "lucide-react";
import { useState, type FC } from "react";

import LoadingSpinner from "@/app/_module/components/LoadingSpinner";
import RenderIf from "@/app/_module/components/RenderIf";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/app/_module/components/ui/table";

import {
  useActivateSingleLeadFromImport,
  useActivateLeadsImportById,
  useGetLeadsImportById,
} from "../hooks/leads-import-queries";
import type { TGetLeadsImportbyIdResponse } from "../types/lead-import-types";

// Failure messages can contain internal storage URLs with signed tokens
// (older rows stored them verbatim) — never render or export those.
const scrubMessage = (m: string | null | undefined): string =>
  (m || "")
    .replace(/https?:\/\/\S+/gi, "[file]")
    .replace(/token=[\w.-]+/gi, "token=[redacted]");


// Helper to extract name from dynamic rawDataSnapshot
const extractName = (snapshot: Record<string, any> | null | undefined): string => {
  // rawDataSnapshot is nullable in the database and every field below is read off it, so
  // a single row without one crashed the whole imported-leads list with "Cannot read
  // properties of null (reading 'firstname')". A missing snapshot just means the original
  // CSV row wasn't retained — the import is still perfectly valid — so it renders as '-'
  // like any other unknown name rather than taking the page down.
  if (!snapshot || typeof snapshot !== 'object') return '-';

  // Try different combinations of name fields
  const firstName = snapshot.firstname || snapshot.firstName || snapshot.first_name || snapshot.Firstname || '';
  const lastName = snapshot.lastname || snapshot.lastName || snapshot.last_name || snapshot.Lastname || '';

  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }

  // Try full name field
  const fullName = snapshot.name || snapshot.Name || snapshot.fullname || snapshot.full_name || '';
  if (fullName) return fullName;

  return '-';
};

// Helper to extract email from dynamic rawDataSnapshot
const extractEmail = (snapshot: Record<string, any> | null | undefined): string => {
  // Same nullable-snapshot guard as extractName — see the note there.
  if (!snapshot || typeof snapshot !== 'object') return '-';
  return snapshot.email || snapshot.Email || snapshot.email_address || snapshot.emailAddress || '-';
};

// Helper to format phone number as (xxx) xxx-xxxx
const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Handle 10-digit numbers (standard US format)
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Handle 11-digit numbers (with country code 1)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // Return original if not a standard format
  return phone;
};

// Helper to extract phone from dynamic rawDataSnapshot
const extractPhone = (snapshot: Record<string, any> | null | undefined): string => {
  // Same nullable-snapshot guard as extractName — see the note there.
  if (!snapshot || typeof snapshot !== 'object') return '-';
  // ⚠️ THE MAPPED `phone` WINS, AND AN EMPTY MAPPED PHONE STAYS EMPTY.
  //
  // This used to try phone_home FIRST and then fall through a list of other
  // columns, so a row whose mapped phone was blank still rendered a number
  // scavenged from somewhere else in the file. The result was a row displaying
  // "(231) 000-0000" next to the error "no phone number", which reads as the
  // validator being broken when it was right — the column the import actually
  // read was empty, and the number on screen came from a different one.
  //
  // If the snapshot carries a mapped `phone` key at all, that is the value the
  // importer judged and the only honest thing to show, empty included.
  if ('phone' in snapshot) {
    const mapped = String(snapshot.phone ?? '').trim();
    return mapped ? formatPhoneNumber(mapped) : '-';
  }

  // No mapped phone key (older snapshots) — fall back to the usual suspects.
  const rawPhone = snapshot.phone_home || snapshot.Phone || snapshot.phone_mobile ||
         snapshot.phoneNumber || snapshot.phone_number || snapshot.mobile || snapshot.cell || '';

  if (!rawPhone) return '-';

  return formatPhoneNumber(String(rawPhone));
};

interface IImportedLeadListDetails {
  leadListId: string;
}

const getStatusBadge = (status: string | undefined, action?: string) => {
  if (!status) return null;

  // Label each row by what actually happened so uploaders see exactly why a
  // lead did or didn't import:
  //   - skipped (warning)  → DUPLICATE (already in the CRM)
  //   - failure            → ERROR (bad/missing email, phone, validation, etc.)
  //   - created            → NEW
  //   - updated            → UPDATED
  // The specific reason text is shown alongside (see the Action column).
  if (action === "skipped") {
    return <Badge className="bg-amber-100 text-amber-800">DUPLICATE</Badge>;
  }
  if (status === "failure") {
    return <Badge className="bg-red-100 text-red-800">ERROR</Badge>;
  }
  if (action === "created") {
    return <Badge className="bg-green-100 text-green-800">NEW</Badge>;
  }
  if (action === "updated") {
    return <Badge className="bg-blue-100 text-blue-800">UPDATED</Badge>;
  }
  // Any other non-success row (e.g. a warning that isn't a duplicate) — still
  // label it clearly and show its reason rather than a vague gray badge.
  if (status === "warning") {
    return <Badge className="bg-amber-100 text-amber-800">SKIPPED</Badge>;
  }

  const statusColors = {
    ready_for_activation: "bg-yellow-100 text-yellow-800",
    failure: "bg-red-100 text-red-800",
    success: "bg-green-100 text-green-800",
    completed: "bg-green-100 text-green-800",
    active: "bg-blue-100 text-blue-800",
  };

  return (
    <Badge
      className={
        statusColors[status as keyof typeof statusColors] ||
        "bg-gray-100 text-gray-800"
      }
    >
      {status.replace("_", " ").toUpperCase()}
    </Badge>
  );
};

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleString();
};

const ImportedLeadListDetails: FC<IImportedLeadListDetails> = function ({
  leadListId,
}) {
  const param = {
    includeFullData: true,
  };

  const { data: LeadsImportDetail, isPending } = useGetLeadsImportById(
    leadListId,
    param
  );
  const { mutate, isPending: isActivateLeadPending } =
    useActivateSingleLeadFromImport();
  // Which rows the results table shows. "all" until a chip is tapped.
  const [rowFilter, setRowFilter] = useState<"all" | "created" | "updated" | "skipped" | "failure">("all");

  const { mutate: reImportAll, isPending: isReImportPending } =
    useActivateLeadsImportById();

  const handleActivateLead = (resultId: string) => {
    mutate({ importId: leadListId, resultId });
  };

  const handleReImportAll = () => {
    reImportAll({ importId: leadListId, param: { bypassImmediateCheck: true } });
  };

  // ⚠️ ROOT IS w-full, NOT w-[95vw].
  //
  // This used to size itself to the VIEWPORT, so dropping it inside anything
  // narrower than the screen — the assign modal's Import Details overlay — made
  // it ~1094px wide inside an ~880px dialog, and the right-hand column ("Entity
  // Type", "Completed At") plus the error text were simply cut off with no
  // scrollbar to reveal them. Filling the container works in both places: the
  // standalone Leads Lists modal still caps at 1110px, and an embedded use fits
  // whatever it is given.
  return (
    <div className="w-full max-w-[1110px] min-w-0 mx-auto overflow-y-auto py-2">
      <RenderIf condition={isPending}>
        <LoadingSpinner />
      </RenderIf>

      <RenderIf condition={!isPending}>
        <div className="space-y-6">
          {/* Import Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Import Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="grid gap-4 min-w-0"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
              >
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Import Name
                  </p>
                  <p className="text-lg font-semibold">
                    {LeadsImportDetail?.name}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Status
                  </p>
                  <div className="mt-1">
                    {getStatusBadge(LeadsImportDetail?.status)}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    File Name
                  </p>
                  <p
                    className="text-sm truncate"
                    title={LeadsImportDetail?.fileName}
                  >
                    {LeadsImportDetail?.fileName}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Entity Type
                  </p>
                  <p className="text-lg font-semibold">
                    {LeadsImportDetail?.targetEntityType}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Total Records
                  </p>
                  <p className="text-lg font-semibold">
                    {LeadsImportDetail?.totalRecords}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Processed Records
                  </p>
                  <p className="text-lg font-semibold">
                    {LeadsImportDetail?.processedRecords}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Started At
                  </p>
                  <p className="text-sm">
                    {formatDate(LeadsImportDetail?.startedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Completed At
                  </p>
                  <p className="text-sm">
                    {formatDate(LeadsImportDetail?.completedAt)}
                  </p>
                </div>
              </div>
              {!!LeadsImportDetail?.errorMessage && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">
                      {LeadsImportDetail?.errorMessage}
                    </p>
                  </div>
                  {/* "Completed with 10 errors" told you a number and nothing else —
                      the reasons were in the results table, somewhere among 2,400
                      rows, so nobody ever found them. Group the failures by reason
                      here: almost always a handful of causes, and knowing it was
                      "no phone number" on 10 rows is the difference between fixing
                      the file and shrugging at it. */}
                  {(() => {
                    const failed = (LeadsImportDetail?.results ?? []).filter(
                      (r: any) => r.status === "failure",
                    );
                    if (failed.length === 0) return null;
                    const byReason = new Map<string, string[]>();
                    for (const r of failed) {
                      const reason = String((r as any).message || "Unknown error").trim();
                      const who =
                        extractName((r as any).rawDataSnapshot as Record<string, any>) ||
                        extractPhone((r as any).rawDataSnapshot as Record<string, any>) ||
                        `Row ${(r as any).sourceRowNumber ?? "?"}`;
                      byReason.set(reason, [...(byReason.get(reason) ?? []), who]);
                    }
                    return (
                      <ul className="mt-2 ml-6 space-y-1.5">
                        {Array.from(byReason.entries())
                          .sort((a, b) => b[1].length - a[1].length)
                          .map(([reason, who]) => (
                            <li key={reason} className="text-sm text-red-800 break-words">
                              <span className="font-semibold">{who.length}×</span> {reason}
                              <span className="block text-xs text-red-700/80 break-words">
                                {who.slice(0, 6).join(", ")}
                                {who.length > 6 ? ` +${who.length - 6} more` : ""}
                              </span>
                            </li>
                          ))}
                      </ul>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Import Results */}
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Import Results ({LeadsImportDetail?.results?.length || 0}{" "}
                records)
              </CardTitle>
              <Button
                onClick={handleReImportAll}
                disabled={isReImportPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isReImportPending ? 'animate-spin' : ''}`} />
                {isReImportPending ? 'Re-importing...' : 'Re-import All'}
              </Button>
            </CardHeader>
            <CardContent>
              {/* At-a-glance breakdown: new / duplicates / errors. Computed from
                  the per-row results so uploaders instantly see why "only X went
                  through" (e.g. 25 new, 97 already in the CRM). */}
              <RenderIf condition={!!LeadsImportDetail?.results?.length}>
                {(() => {
                  const rows = LeadsImportDetail?.results ?? [];
                  const newCount = rows.filter((r) => r.action === "created").length;
                  const dupCount = rows.filter((r) => r.action === "skipped").length;
                  const updCount = rows.filter((r) => r.action === "updated").length;
                  const errCount = rows.filter((r) => r.status === "failure").length;
                  // Clickable filters. Ten failures inside 2,400 rows are
                  // unfindable by scrolling; one tap on "10 errors" shows only those.
                  const chip = (key: typeof rowFilter, active: string, idle: string, label: string) => (
                    <button
                      type="button"
                      onClick={() => setRowFilter((cur) => (cur === key ? "all" : key))}
                      className={`rounded-full px-2.5 py-1 font-medium transition-colors ${rowFilter === key ? active : idle}`}
                      aria-pressed={rowFilter === key}
                    >
                      {label}
                    </button>
                  );
                  return (
                    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                      {chip("created", "bg-green-600 text-white", "bg-green-100 text-green-800 hover:bg-green-200", `${newCount} new`)}
                      {updCount > 0 && chip("updated", "bg-blue-600 text-white", "bg-blue-100 text-blue-800 hover:bg-blue-200", `${updCount} updated`)}
                      {dupCount > 0 && chip("skipped", "bg-amber-600 text-white", "bg-amber-100 text-amber-800 hover:bg-amber-200", `${dupCount} duplicate${dupCount === 1 ? "" : "s"} (already in CRM)`)}
                      {errCount > 0 && chip("failure", "bg-red-600 text-white", "bg-red-100 text-red-800 hover:bg-red-200", `${errCount} error${errCount === 1 ? "" : "s"}`)}
                      {rowFilter !== "all" && (
                        <button type="button" onClick={() => setRowFilter("all")} className="text-gray-500 underline underline-offset-2 hover:text-gray-700">
                          show all
                        </button>
                      )}
                    </div>
                  );
                })()}
                <div className="border rounded-md">
                  <div className="max-h-[450px] overflow-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white z-10">
                        <TableRow>
                          <TableHead className="min-w-[150px]">Name</TableHead>
                          <TableHead className="min-w-[200px]">Email</TableHead>
                          <TableHead className="min-w-[120px]">Phone</TableHead>

                          <TableHead className="min-w-[100px]">
                            Status
                          </TableHead>
                          <TableHead className="min-w-[250px]">
                            Action
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(LeadsImportDetail?.results ?? [])
                          .filter((r: any) =>
                            rowFilter === "all"
                              ? true
                              : rowFilter === "failure"
                                ? r.status === "failure"
                                : r.action === rowFilter,
                          )
                          .map(
                          (
                            result: TGetLeadsImportbyIdResponse["results"][0]
                          ) => (
                            <TableRow key={result.id}>
                              <TableCell>
                                {extractName(result.rawDataSnapshot as Record<string, any>)}
                              </TableCell>
                              <TableCell>
                                {extractEmail(result.rawDataSnapshot as Record<string, any>)}
                              </TableCell>
                              <TableCell>
                                {extractPhone(result.rawDataSnapshot as Record<string, any>)}
                              </TableCell>

                              <TableCell>
                                {getStatusBadge(result.status, result.action)}
                              </TableCell>
                              <TableCell>
                                {result.status === "failure" ||
                                result.status === "warning" ||
                                result.action === "skipped" ? (
                                  // Didn't import — show WHY (duplicate of which
                                  // lead, or the error: bad email / no phone /
                                  // validation / any other reason) instead of an
                                  // Activate button that doesn't apply. The reason
                                  // is the backend's verbatim per-row message, so
                                  // every error type surfaces — not a fixed list.
                                  <span
                                    className={`text-xs ${result.status === "failure" ? "text-red-700" : "text-amber-700"}`}
                                    title={scrubMessage(result.message) || undefined}
                                  >
                                    {scrubMessage(result.message)?.replace(/^(Skipped|Failed):\s*/i, "") ||
                                      (result.status === "failure" ? "Could not import" : "Already in your CRM")}
                                  </span>
                                ) : (
                                  <Button
                                    className="px-3 h-7 text-xs cursor-pointer bg-green-100 text-green-800 hover:bg-green-300"
                                    disabled={isActivateLeadPending}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleActivateLead(result.id);
                                    }}
                                  >
                                    Activate Lead
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </RenderIf>
            </CardContent>
          </Card>
        </div>
      </RenderIf>
    </div>
  );
};

export default ImportedLeadListDetails;
